// React hook for ModIQ project management with Firebase
import { useState, useEffect, useCallback } from 'react';
import { 
  saveModIQProject, 
  loadModIQProject, 
  listUserProjects, 
  deleteModIQProject,
  updateProjectName,
  autoSaveProject,
  extractModIQState 
} from '../services/modiqDataService';

export const useModIQProject = (userId = 'anonymous') => {
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [projects, setProjects] = useState([]);

  // Load project list - moved up to avoid initialization order issues
  const loadProjectList = useCallback(async () => {
    try {
      const result = await listUserProjects(userId);
      if (result.success) {
        setProjects(result.projects);
      }
      return result;
    } catch (error) {
      console.error('Error loading project list:', error);
      return { success: false, error: error.message };
    }
  }, [userId]);

  // Save current project
  const saveProject = useCallback(async (modiqState, projectId = null, name = null) => {
    console.log('🔄 saveProject called with:', {
      projectId: projectId,
      name: name,
      currentProjectId: currentProjectId
    });
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const projectData = extractModIQState(modiqState);
      if (name) projectData.projectName = name;
      
      console.log('🔄 Saving project with name:', name || projectData.projectName);
      console.log('🔄 Using project ID:', projectId, '(null means create new)');
      
      const result = await saveModIQProject(projectData, projectId, userId);
      
      if (result.success) {
        console.log('✅ Save result:', {
          success: true,
          projectId: result.projectId,
          wasUpdate: !!projectId,
          wasCreate: !projectId
        });
        
        setCurrentProjectId(result.projectId);
        
        // Use the name we passed in, or fall back to the returned data
        const finalProjectName = name || result.data.projectName || projectData.projectName;
        console.log('📝 Setting project name to:', finalProjectName);
        setProjectName(finalProjectName);
        setLastSaved(new Date());
        
        // Refresh project list
        await loadProjectList();
        
        return { success: true, projectId: result.projectId };
      } else {
        setSaveError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      setSaveError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  }, [userId, loadProjectList]);

  // Load project
  const loadProject = useCallback(async (projectId) => {
    setIsLoading(true);
    setSaveError(null);
    
    try {
      const result = await loadModIQProject(projectId);
      
      if (result.success) {
        console.log('📂 Loading project data:', {
          projectId,
          projectName: result.data.projectName,
          updatedAt: result.data.updatedAt
        });
        
        console.log('📝 Setting currentProjectId to:', projectId);
        setCurrentProjectId(projectId);
        setProjectName(result.data.projectName || 'Untitled Project');
        
        // Handle date parsing for both Firebase timestamps and localStorage dates
        let lastSavedDate = null;
        if (result.data.updatedAt) {
          try {
            // Handle Firebase Timestamp (has toDate method)
            if (result.data.updatedAt.toDate && typeof result.data.updatedAt.toDate === 'function') {
              lastSavedDate = result.data.updatedAt.toDate();
            }
            // Handle regular JavaScript Date or date string
            else {
              const date = new Date(result.data.updatedAt);
              lastSavedDate = isNaN(date.getTime()) ? null : date;
            }
          } catch (error) {
            console.warn('Error parsing updatedAt date:', error);
            lastSavedDate = null;
          }
        }
        setLastSaved(lastSavedDate);
        
        console.log('📝 Project loaded - Name:', result.data.projectName, 'Last Saved:', lastSavedDate);
        
        return { success: true, data: result.data };
      } else {
        setSaveError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      setSaveError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete project
  const deleteProject = useCallback(async (projectId) => {
    try {
      const result = await deleteModIQProject(projectId);
      if (result.success) {
        // If we deleted the current project, clear it
        if (projectId === currentProjectId) {
          setCurrentProjectId(null);
          setProjectName('');
          setLastSaved(null);
        }
        
        // Refresh project list
        await loadProjectList();
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [currentProjectId, loadProjectList]);

  // Update project name
  const updateName = useCallback(async (projectId, newName) => {
    try {
      const result = await updateProjectName(projectId, newName);
      if (result.success) {
        setProjectName(newName);
        await loadProjectList();
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [loadProjectList]);

  // Auto-save with debouncing
  const enableAutoSave = useCallback((modiqState, delay = 3000) => {
    if (currentProjectId) {
      const projectData = extractModIQState(modiqState);
      autoSaveProject(projectData, currentProjectId, userId, delay);
    }
  }, [currentProjectId, userId]);

  // Create new project
  const createNewProject = useCallback((name = '') => {
    setCurrentProjectId(null);
    setProjectName(name);
    setLastSaved(null);
    setSaveError(null);
  }, []);

  // Trigger save (useful for manual saves)
  const triggerSave = useCallback(async (modiqState) => {
    if (projectName) {
      return await saveProject(modiqState, currentProjectId, projectName);
    }
    return { success: false, error: 'No project name set' };
  }, [saveProject, currentProjectId, projectName]);

  // Load project list on mount
  useEffect(() => {
    loadProjectList();
  }, [loadProjectList]);

  return {
    // State
    currentProjectId,
    projectName,
    isSaving,
    isLoading,
    lastSaved,
    saveError,
    projects,
    
    // Actions
    saveProject,
    loadProject,
    loadProjectList,
    deleteProject,
    updateName,
    enableAutoSave,
    createNewProject,
    triggerSave,
    
    // Utilities
    setProjectName,
    setCurrentProjectId
  };
};

export default useModIQProject; 